import { UntypedFormControl } from '@angular/forms';
import { FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormFieldType } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms/validators';
import { ComplaintPreferences } from '@shared/models/document-preferences.model';
import { AddressLookupService } from '@shared/services';
import { FindAddressResponse } from './address';

function stateWithCallback<T>(initial: T, callback: ((newValue: T) => void) | undefined) {
  let currentValue = initial;
  return {
    get value() {
      return currentValue;
    },
    set value(newValue) {
      if (currentValue !== newValue) {
        currentValue = newValue;
        // eslint-disable-next-line callback-return
        callback?.(newValue);
      }
    },
  };
}

/**
 * Create a complaints sub-form. This closure encapsulates the state required for the form (e.g. whether it's a manual
 * address, whether the search was successful).
 * This can either be used to create a stand-alone form (such as for distribution group management) or can be embedded into
 * another form (such as for user document preferences).
 */
export function createComplaintsFormFields(
  addressLookupService: AddressLookupService,
  model: ComplaintPreferences | (() => ComplaintPreferences | null),
  options: {
    isManualAddress?: boolean;
    onIsManualAddressChange?: (newValue: boolean) => void;
    onAddressSearchSuccessfulChange?: (newValue: boolean) => void;
    onAddressesChange?: (newValue: FindAddressResponse[]) => void;
  } = {}) {

  const isManualAddress = stateWithCallback(options.isManualAddress ?? false, options.onIsManualAddressChange);
  const addressSearchSuccessful = stateWithCallback<boolean>(false, options.onAddressSearchSuccessfulChange);
  const availableAddresses = stateWithCallback<FindAddressResponse[]>([], options.onAddressesChange);

  function getModel() {
    return typeof model === 'function' ? model() : model;
  }

  function searchAddress(postcodeField: FormlyFieldConfig): void {
    const postcode: string | undefined = postcodeField.formControl?.value;
    if (!postcode || !FormsValidators.postcodeRegex.test(postcode)) {
      return;
    }

    addressLookupService.findAddress(postcode).subscribe(addresses => {
      addressSearchSuccessful.value = addresses.length > 0;

      const addressFieldConfig: FormlyFieldConfig | undefined = postcodeField.parent?.fieldGroup
        ?.find(x => x.key === 'addressLineLookUp' && x.type === FormFieldType.Select);
      if (addressFieldConfig?.props && addressSearchSuccessful.value) {
        addressFieldConfig.props.options = addresses.map(a => ({
          value: a.id,
          label: a.text,
        }));
        availableAddresses.value = addresses;
      }

      postcodeField.formControl?.updateValueAndValidity();
    });
  }

  function prePopAddress(lookupField: FormlyFieldConfig) : void {
    const fc = lookupField.formControl;
    const selectedAddressId = fc?.value as string | undefined;
    if (!selectedAddressId?.length) {
      return;
    }

    addressLookupService.retrieveAddress(selectedAddressId).subscribe(newAddress => {
      const [firstLine, ...secondLine] = newAddress.lines;

      Object.assign(getModel() ?? {}, {
        addressLine1: firstLine.trim(),
        addressLine2: secondLine.join(', ').trim(),
        town: newAddress.town.trim(),
        county: newAddress.county,
        postcode: newAddress.postcode,
      });
    });
  }

  const fields: FormlyFieldConfig<FormlyFieldProps & { [additionalProperties: string]: any }>[] = [
    {
      key: 'contactPerson',
      type: FormFieldType.Input,
      props: {
        label: 'Contact person / department name',
        required: true,
        maxLength: 100,
      },
    },
    {
      key: 'postcode',
      type: FormFieldType.PostCode,
      props: {
        label: 'Complaints postcode',
        submitText: 'Find address',
        linkText: 'Enter address manually',
        required: true,
        maxLength: 10,
        setAddressType: () => isManualAddress.value = !isManualAddress.value,
        clickHandler: (field: FormlyFieldConfig) => searchAddress(field),
      },
      expressions: {
        hide: () => isManualAddress.value,
      },
      validators: {
        validation: [
          FormsValidators.postcode,
        ],
      },
    },
    {
      key: 'addressLineLookUp',
      type: FormFieldType.Select,
      props: {
        label: 'Complaints address',
        placeholder: 'Please select...',
        options: [],
        required: true,
        change: field => prePopAddress(field),
      },
      expressions: {
        hide: () => !addressSearchSuccessful.value || isManualAddress.value,
      },
    },
    {
      key: 'addressLine1',
      type: FormFieldType.Input,
      props: {
        label: 'Address line 1',
        required: true,
        maxLength: 255,
      },
      expressions: {
        hide: () => !isManualAddress.value,
      },
    },
    {
      key: 'addressLine2',
      type: FormFieldType.Input,
      props: {
        label: 'Address line 2',
        maxLength: 255,
      },
      expressions: {
        hide: () => !isManualAddress.value,
      },
    },
    {
      key: 'town',
      type: FormFieldType.Input,
      props: {
        label: 'Town',
        required: true,
        maxLength: 100,
      },
      expressions: {
        hide: () => !isManualAddress.value,
      },
    },
    {
      key: 'county',
      type: FormFieldType.Input,
      props: {
        label: 'County',
        required: false,
        maxLength: 100,
      },
      expressions: {
        hide: () => !isManualAddress.value,
      },
    },
    {
      key: 'postcode',
      type: FormFieldType.PostCode,
      props: {
        label: 'Postcode',
        linkText: 'Search for address',
        setAddressType: () => isManualAddress.value = !isManualAddress.value,
        required: true,
        maxLength: 10,
      },
      expressions: {
        hide: () => !isManualAddress.value,
      },
    },
    {
      key: 'telephone',
      type: FormFieldType.Input,
      props: {
        label: 'Telephone number',
        required: true,
        maxLength: 15,
      },
      validators: {
        telephone: {
          expression: (fc: UntypedFormControl) => !FormsValidators.telephone(fc),
          message: 'Enter valid telephone number',
        },
      },
    },
    {
      key: 'emailAddress',
      type: FormFieldType.Input,
      props: {
        label: 'Email address',
        inputType: 'email',
        required: false,
        maxLength: 100,
      },
      validators: {
        validation: [FormsValidators.email],
      },
    },
    {
      key: 'maxHandlingTime',
      type: FormFieldType.Number,
      props: {
        label: 'Max time for handling complaints (weeks)',
        min: 0,
        required: true,
      },
      validators: {
        validation: [
          FormsValidators.integer,
        ],
      },
    },
  ];

  /** Resets the states for these fields. */
  function resetState() {
    isManualAddress.value = false;
    addressSearchSuccessful.value = false;
    availableAddresses.value = [];

    const addressPickerFieldConfig = fields.find(f => f.name === 'addressLineLookUp');
    if (addressPickerFieldConfig?.props) {
      addressPickerFieldConfig.props.options = [];
    }
  }

  return { fields, resetState };
}
